import dayjs from "dayjs";
import moment from "moment";
import { changeLocalDateTimeToUTC } from "./formatDate";
import momentTz from "moment-timezone";
/**
 * Format date according to the provided format.
 *
 * @param {String} date
 * @param {String} format = 'YYYY-MM-DD'
 * @returns {String}
 */
export function getFormattedDate(date, format = "YYYY-MM-DD") {
  return dayjs(date).format(format);
}

/**
 * Whether two dates are the same day, in any format; two empty dates match.
 */
export function isSameDay(date, otherDate) {
  if (!date || !otherDate) return !date && !otherDate;

  return dayjs(date).isSame(dayjs(otherDate), "day");
}

export function getDateJsFromDateString(date, format = "YYYY-MM-DD") {
  const momentDateString = moment.utc(date).format(format);
  return dayjs(momentDateString);
}

/**
 * Convert the calendar day picked in the browser to the start of that day in
 * UTC, the way the backend saves date-only values.
 *
 * @param {String|Date|dayjs.Dayjs} date
 * @returns {String|null}
 */
export const toUTCStartOfDayISOString = (date) =>
  date ? moment.utc(dayjs(date).format("YYYY-MM-DD")).toISOString() : null;

export const getFormattedDateTime = () => {
  let date = new Date();
  date = getFormattedDate(date, "MM-DD-YYYY-HH-mm-ss");

  return date;
};
export const formatLocalDateTime = (date, format = "MM/DD/YYYY") => {
  return moment(date).format(format);
};

export const formatDateTime = (date, format = "MM/DD/YYYY") => {
  return moment.utc(date).format(format);
};

export const isValidDateFormat = (dateString, format = "YYYY/MM/DD") => {
  const date = moment(dateString, format, true);
  return date.isValid();
};

export const formatIsoDate = (date) => {
  const nDate = new Date(date);
  return `${nDate.getMonth() + 1}/${nDate.getDate()}/${nDate.getFullYear()}`;
};

export const isDateInPast = (date) => {
  const today = moment().startOf("day");
  const parsedDate = moment(date, "YYYY-MM-DD", true);
  return parsedDate.isValid() && parsedDate.isBefore(today);
};

export const isDateInFuture = (date) => {
  const today = moment().startOf("day");
  const parsedDate = moment(date, "YYYY-MM-DD", true);
  return parsedDate.isValid() && parsedDate.isAfter(today);
};

export const getDateAndTimeWithAMPM = (date, format = "MM/DD/YYYY") => {
  const formatDate = moment(date).format(format);
  const time = moment(date).format("hh:mm a");
  return {
    date: formatDate,
    time: time,
  };
};

export const addToDate = (date, unit = 0, type = "days") => {
  return moment(date).add(unit, type);
};

export const subtractFromDate = (date, unit = 0, type = "days") => {
  return moment(date).subtract(unit, type);
};

export const getStartOfPastMonth = (monthsToSubtract) => {
  return moment().utc().subtract(monthsToSubtract, "months").startOf("month");
};

export const generateDateRangeArray = (startDate, endDate = new Date()) => {
  const start = moment(startDate);
  const end = moment(endDate);

  const dateArray = [];

  let currentDate = moment(start);
  while (currentDate.isSameOrBefore(end, "day")) {
    dateArray.push(getFormattedDate(currentDate, "MM/DD/YYYY"));
    currentDate.add(1, "day");
  }

  return dateArray;
};

/**
 * Formats a date to: "Mon Dec 02 2024 14:25:26 GMT+0545"
 * @param {Date | string | number} date - The date to format (optional, defaults to current date/time)
 * @returns {string} - Formatted date string
 */
export const formatDateToCustomGMT = (date = new Date()) => {
  // Parse the input date
  const parsedDate = dayjs(date);
  // Check if the input is valid
  if (!parsedDate.isValid()) {
    throw new Error(
      "Invalid date provided. Please provide a valid Date, timestamp, or ISO string."
    );
  }

  // Format the date
  return parsedDate.format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZ");
};

/**
 * Validates if the given value is a valid date and falls within a realistic year range.
 *
 * @param {string} value - The date string to validate.
 * @param {number} [minYear=1900] - The minimum acceptable year.
 * @param {number} [maxYearOffset=10] - The maximum offset from the current year.
 * @returns {boolean} - Returns `true` if the date is valid and within the year range, otherwise `false`.
 */
export const isValidDate = (value, minYear = 1900, maxYearOffset = 10) => {
  // Required field validation
  if (!value) return false;

  const parsedDate = new Date(value);

  // Check if it's a valid date
  if (isNaN(parsedDate.getTime())) return false;

  // Extract year and validate it
  const year = parsedDate.getFullYear();
  const currentYear = new Date().getFullYear();

  // Define a realistic range for years
  return year >= minYear && year <= currentYear + maxYearOffset;
};

export const getCurrentYearAndMonth = () => {
  const currentDate = new Date();
  return {
    currentYear: currentDate.getFullYear(),
    currentMonth: currentDate.getMonth() + 1, // getMonth() returns the index starting from 0
  };
};

export const getDiffDaysFromCurrentDate = (pastDate) => {
  if (!pastDate || !isValidDate(pastDate)) {
    throw new Error("Passed date is not valid");
  }

  const currentDateTime = new Date();
  const pastDateTime = new Date(pastDate);

  currentDateTime.setHours(0, 0, 0, 0);
  pastDateTime.setHours(0, 0, 0, 0);

  const diffInMs = currentDateTime - pastDateTime;
  const diffInDays = diffInMs / (24 * 60 * 60 * 1000);
  return Math.floor(diffInDays);
};

export const getStartDate = (unit = "month", format = "YYYY-MM-DD") => {
  return moment().startOf(unit).format(format);
};

export const getEndDate = (unit = "month", format = "YYYY-MM-DD") => {
  return moment().endOf(unit).format(format);
};

export const formatCustomDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = moment(dateString);
  if (!date.isValid()) return "—";
  return `${date.format("MM/DD/YYYY")}\n${date.format("hh:mm A")}`;
};

/**
 * Get duration difference between two dates.
 * @param {string|Date} startDate
 * @param {string|Date} endDate
 * @param {string} [format='m[m] s[s]'] - Options: 'm[m] s[s]', 'h[h] m[m]', 'HH:mm:ss'
 * @returns {string}
 */
export const getDurationDiff = (startDate, endDate, format = "m[m] s[s]") => {
  const diff = Math.abs(moment(endDate).diff(moment(startDate)));
  const duration = moment.duration(diff);

  if (format === "h[h] m[m]") {
    return `${Math.floor(duration.asHours())}h ${duration.minutes()}m`;
  } else if (format === "HH:mm:ss") {
    const h = String(Math.floor(duration.asHours())).padStart(2, "0");
    const m = String(duration.minutes()).padStart(2, "0");
    const s = String(duration.seconds()).padStart(2, "0");
    return `${h}:${m}:${s}`;
  }

  return `${duration.minutes()}m ${duration.seconds()}s`;
};

export const formatDateRange = (value) => {
  const startDate = changeLocalDateTimeToUTC(value, "YYYY-MM-DDTHH:mm:ss");
  const endDate = changeLocalDateTimeToUTC(
    moment(new Date(value)).add(1, "days"),
    "YYYY-MM-DDTHH:mm:ss"
  );

  return `${startDate},${endDate}`;
};

export const getDateDifference = ({
  start,
  end,
  unit = "days",
  precise = true,
}) => {
  const startDate = moment(start);
  const endDate = moment(end);
  return endDate.diff(startDate, unit, precise);
};

export const formatTimeAgo = (date) => {
  if (!date) return "";
  const mins = moment().diff(moment(date), "minutes");
  if (mins < 60) return `${mins} m ago`;
  const hrs = moment().diff(moment(date), "hours");
  if (hrs < 24) return `${hrs} h ago`;
  return `${moment().diff(moment(date), "days")} d ago`;
};

/**
 * Converts a date to a specified timezone and format
 * @param {String|Date} date - date to be converted
 * @param {String} timeZone - timezone to convert the date to
 * @param {String} format - desired date format
 * @returns {String} - formatted date string in the specified timezone
 */
export const convertMTDateToUTC = (value, isEndDate = false) => {
  const dateInMT = isEndDate
    ? momentTz.tz(value, "America/Denver").endOf("day")
    : momentTz.tz(value, "America/Denver").startOf("day");
  return dateInMT.utc().format("YYYY-MM-DDTHH:mm:ss");
};

export const convertDateToTimezone = (
  date,
  timeZone = "UTC",
  format = "MM/DD/YYYY"
) => {
  const timezone = new Intl.DateTimeFormat("en-US", {
    timeZone,
  }).resolvedOptions().timeZone;

  return momentTz.tz(date, timezone).format(format);
};
